import { BrakeGeometries, BrakePunchDie, BrakeTooling, convexDecomposition, fetchPost, ParsedBrakeDie, ParsedBrakePunch } from "@oshcut/oshlib"
import { ExtrudeGeometry } from "three"
import { createShape } from "./createBendModel"

const extrudeSettings = {
  steps: 1,
  depth: 1,
  bevelEnabled: false
}

/**
 * An array containing the brake tooling information and geometries.
 */
export const machines:
  {
    machineId: number
    tooling: BrakeTooling
    geometries: BrakeGeometries
  }[] = []

/**
 * A Promise which resolves with an object containing the brake tooling information.
 */
export const toolingPromise = fetchPost('/api/v2/brake/tooling', { version: 2 })
  .then((result: any) => {

    for (let tooling of result.machines) {

      const geometries: BrakeGeometries =
      {
        dies: { ['profile']: {}, ['collision_profile']: {} },
        punches: { ['profile']: {}, ['collision_profile']: {} },
        punchHolder: { ['profile']: getEmptyProfileGeometry(), ['collision_profile']: getEmptyProfileGeometry() },
        dieHolder: { ['profile']: getEmptyProfileGeometry(), ['collision_profile']: getEmptyProfileGeometry() },
        machineBed: { ['profile']: getEmptyProfileGeometry(), ['collision_profile']: getEmptyProfileGeometry() },
        machineTop: { ['profile']: getEmptyProfileGeometry(), ['collision_profile']: getEmptyProfileGeometry() },
        leftBackgauge: { ['profile']: getEmptyProfileGeometry(), ['collision_profile']: getEmptyProfileGeometry() },
        rightBackgauge: { ['profile']: getEmptyProfileGeometry(), ['collision_profile']: getEmptyProfileGeometry() },
        frontLaserEye: { ['profile']: getEmptyProfileGeometry(), ['collision_profile']: getEmptyProfileGeometry() },
        rearLaserEye: { ['profile']: getEmptyProfileGeometry(), ['collision_profile']: getEmptyProfileGeometry() },
        extrudeSettings
      }

      // Create unscaled, untranslated geometries
      const profileKeys = ['profile', 'collision_profile'] as const
      const geometryKeys = ['punchHolder', 'dieHolder', 'machineBed', 'machineTop', 'leftBackgauge', 'rightBackgauge', 'frontLaserEye', 'rearLaserEye'] as const
      for (let geo of geometryKeys) {
        for (let profile of profileKeys) {
          let geometry = new ExtrudeGeometry(createShape(tooling[geo][profile]), geometries.extrudeSettings)
          let convexDecomp = convexDecomposition(tooling[geo][profile])
          let convexGeoms = convexDecomp.map(decomp =>
            new ExtrudeGeometry(createShape(decomp), geometries.extrudeSettings)
          )
          geometries[geo][profile] = {
            geometry,
            convexDecomp,
            convexGeoms
          }
        }

        // Prevent inadvertent object pollution after a recent refactoring
        Object.freeze(geometries[geo])
      }

      // Prevent inadvertent object pollution after a recent refactoring
      Object.freeze(geometries.punches)
      Object.freeze(geometries.dies)

      machines.push({
        machineId: tooling.machineId,
        tooling,
        geometries
      })
    }

    return result
  })

function getEmptyProfileGeometry() {
  return {
    geometry: new ExtrudeGeometry(createShape([]), extrudeSettings),
    convexDecomp: [],
    convexGeoms: []
  }
}

/**
 * Retrieves the punch and die for a given punch/die pair.
 * @param punchDiePair The punch/die pair to retrieve.
 * @returns The punch and die for the given pair.
 * @throws Error if the punch/die pair could not be found.
 */
export function getPunchAndDie(punchDiePair: BrakePunchDie): { punch: ParsedBrakePunch, die: ParsedBrakeDie } {
  let machine = machines.find(t => t.machineId === punchDiePair.machine_id)
  if (!machine) {
    throw new Error(`Could not find machine ${punchDiePair.machine_id}`)
  }
  let punch = machine.tooling.punches.find(p => p.id === punchDiePair.punch)
  if (!punch) {
    throw new Error(`Could not find punch ${punchDiePair.punch}`)
  }
  let die = machine.tooling.dies.find(d => d.id === punchDiePair.die)
  if (!die) {
    throw new Error(`Could not find die ${punchDiePair.die}`)
  }
  return { punch, die }
}

/**
 * Gets a fallback punch/die pair to use when no material is selected in the part.
 */
export function getDefaultPunchDiePair() {
  return machines
    .filter(m => m.tooling.enabled)
    .map(m => m.tooling.punchDiePairs)
    .flat()[0]
}